import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/api"

export interface NotificationConfigItem {
  id: string
  notification_type: string
  label: string
  description: string
  category: string
  available_channels: string[]
  is_enabled: boolean
  email_enabled: boolean
  push_enabled: boolean
  in_app_enabled: boolean
  recurrence: string
  cron_expression: string | null
  email_subject: string | null
  email_body_html: string | null
  push_title: string | null
  push_body: string | null
  in_app_title: string | null
  in_app_body: string | null
  updated_by: string | null
  updated_at: string | null
}

export interface VariableInfo {
  key: string
  description: string
}

export interface RecurrenceOption {
  value: string
  label: string
}

export interface NotificationCatalog {
  notifications: NotificationConfigItem[]
  variables: VariableInfo[]
  recurrence_options: RecurrenceOption[]
}

export function useNotificationConfigs() {
  return useQuery<NotificationCatalog>({
    queryKey: ["notification-configs"],
    queryFn: async () => {
      const { data } = await api.get("/v1/notification-configs/")
      return data
    },
  })
}

export function useUpdateNotificationConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ type, payload }: { type: string; payload: Partial<NotificationConfigItem> }) => {
      const { data } = await api.put(`/v1/notification-configs/${type}`, payload)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notification-configs"] }),
  })
}

export function useResetNotificationConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (type: string) => {
      const { data } = await api.post(`/v1/notification-configs/${type}/reset`)
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notification-configs"] }),
  })
}
